import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, ClipboardList, Clock, CheckCircle, XCircle, Loader2, User, Bed, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

export default function ServiceOrders({ user: propUser, isTab, tabId }) {
  const user = useUser(propUser);
  const [orders, setOrders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [activeGroupTab, setActiveGroupTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    booking_id: '',
    group_id: '',
    service_id: '',
    quantity: '1',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, groupsRes, servicesRes, bookingsRes] = await Promise.all([
        api.get('/service-orders'),
        api.get('/service-groups'),
        api.get('/services'),
        api.get('/bookings?status=checked_in')
      ]);
      setOrders(ordersRes.data);
      setGroups(groupsRes.data);
      setServices(servicesRes.data.filter(s => s.is_available !== false));
      setBookings(bookingsRes.data);
      
      // Set default active tab to first group if exists
      if (groupsRes.data.length > 0 && activeGroupTab === 'all') {
        setActiveGroupTab('all');
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      await api.post('/service-orders', {
        booking_id: formData.booking_id,
        service_id: formData.service_id,
        quantity: parseInt(formData.quantity),
        notes: formData.notes || null
      });
      toast.success('Service order created', { position: 'top-center' });
      setAddDialogOpen(false);
      setFormData({ booking_id: '', group_id: '', service_id: '', quantity: '1', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order', { position: 'top-center' });
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/service-orders/${orderId}/status?status=${newStatus}`);
      toast.success('Order status updated', { position: 'top-center' });
      loadData();
    } catch (error) {
      toast.error('Failed to update status', { position: 'top-center' });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const formatStatus = (status) => {
    if (status === 'pending') return 'Yet to Start';
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get services for selected group in form
  const getServicesForSelectedGroup = () => {
    if (!formData.group_id) return [];
    return services.filter(s => s.group_id === formData.group_id);
  };

  // Filter orders based on active tab, status filter, and search
  const getFilteredOrders = () => {
    let filtered = orders;
    
    // Filter by group tab
    if (activeGroupTab !== 'all') {
      filtered = filtered.filter(o => o.group_id === activeGroupTab);
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(o => 
        o.service_name?.toLowerCase().includes(query) ||
        o.guest_name?.toLowerCase().includes(query) ||
        o.room_number?.toLowerCase().includes(query) ||
        o.notes?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  // Get order counts for a specific group
  const getGroupOrderCounts = (groupId) => {
    const groupOrders = groupId === 'all' 
      ? orders 
      : orders.filter(o => o.group_id === groupId);
    
    return {
      total: groupOrders.length,
      pending: groupOrders.filter(o => o.status === 'pending').length,
      in_progress: groupOrders.filter(o => o.status === 'in_progress').length,
      completed: groupOrders.filter(o => o.status === 'completed').length
    };
  };

  const selectedService = services.find(s => s.id === formData.service_id);
  const totalAmount = selectedService ? selectedService.price * parseInt(formData.quantity || 1) : 0;
  const filteredOrders = getFilteredOrders();

  // Reset service when group changes
  const handleGroupChange = (groupId) => {
    setFormData({ ...formData, group_id: groupId, service_id: '' });
  };

  return (
    <div className="space-y-6" data-testid="service-orders">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>Service Orders</h1>
          <p className="text-muted-foreground mt-1">Manage guest service orders by room</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} data-testid="new-order-button">
          <Plus className="h-4 w-4 mr-2" />
          New Service Order
        </Button>
      </div>

      {/* New Order Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Service Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrder} className="space-y-4">
            {/* Step 1: Select Room/Guest */}
            <div className="space-y-2">
              <Label>1. Select Room/Guest</Label>
              <Select
                value={formData.booking_id}
                onValueChange={(value) => setFormData({ ...formData, booking_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a checked-in guest" />
                </SelectTrigger>
                <SelectContent>
                  {bookings.map(booking => (
                    <SelectItem key={booking.id} value={booking.id}>
                      Room {booking.room_number || booking.room_id?.slice(-4)} - {booking.guest_name || `Guest ${booking.guest_id?.slice(-4)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Select Service Group */}
            <div className="space-y-2">
              <Label>2. Select Service Group</Label>
              <Select
                value={formData.group_id}
                onValueChange={handleGroupChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a service group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.filter(g => g.is_active !== false).map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 3: Select Service (only shown after group selected) */}
            {formData.group_id && (
              <div className="space-y-2">
                <Label>3. Select Service</Label>
                <Select
                  value={formData.service_id}
                  onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {getServicesForSelectedGroup().map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} - ₹{service.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {getServicesForSelectedGroup().length === 0 && (
                  <p className="text-sm text-muted-foreground">No services in this group</p>
                )}
              </div>
            )}

            {/* Step 4: Quantity and Notes */}
            {formData.service_id && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Amount</Label>
                    <div className="h-10 px-3 py-2 border rounded-md bg-muted flex items-center font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      ₹{totalAmount}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Special instructions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                type="submit" 
                className="flex-1" 
                disabled={!formData.booking_id || !formData.service_id}
              >
                Create Order
              </Button>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service Group Tabs */}
      <Tabs value={activeGroupTab} onValueChange={setActiveGroupTab}>
        <TabsList className="flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            All Orders
            <Badge className="ml-2 bg-gray-200 text-gray-700">{orders.length}</Badge>
          </TabsTrigger>
          {groups.filter(g => g.is_active !== false).map(group => {
            const counts = getGroupOrderCounts(group.id);
            return (
              <TabsTrigger 
                key={group.id} 
                value={group.id}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {group.name}
                {counts.total > 0 && (
                  <Badge className="ml-2 bg-gray-200 text-gray-700">{counts.total}</Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Yet to Start</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search Field */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by service, guest, room..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Quick Stats for Active Tab */}
          <div className="flex items-center gap-3 ml-auto">
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <Clock className="h-3 w-3 mr-1" />
              {getGroupOrderCounts(activeGroupTab).pending} Pending
            </Badge>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Loader2 className="h-3 w-3 mr-1" />
              {getGroupOrderCounts(activeGroupTab).in_progress} In Progress
            </Badge>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              {getGroupOrderCounts(activeGroupTab).completed} Completed
            </Badge>
          </div>
        </div>

        {/* Orders Content */}
        <TabsContent value={activeGroupTab} className="mt-0">
          {loading ? (
            <div className="text-center py-12">Loading orders...</div>
          ) : filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? 'No orders match your search' : 'No orders found'}
                </p>
                {statusFilter !== 'all' && (
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setStatusFilter('all')}
                  >
                    Clear status filter
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredOrders.map(order => (
                <Card key={order.id} className="hover:shadow-md transition-shadow" data-testid={`order-card-${order.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Badge className={`${getStatusBadge(order.status)} text-[10px] px-1.5 py-0`}>
                            {getStatusIcon(order.status)}
                            <span className="ml-0.5">{formatStatus(order.status)}</span>
                          </Badge>
                        </div>
                        <h3 className="font-medium text-sm truncate">{order.service_name}</h3>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-0.5">
                            <Bed className="h-3 w-3" />
                            {order.room_number || 'N/A'}
                          </span>
                          <span className="truncate">{order.guest_name || 'Guest'}</span>
                        </div>
                        {order.notes && (
                          <p className="text-[10px] text-muted-foreground mt-1 truncate italic">{order.notes}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted-foreground">x{order.quantity}</p>
                        <p className="font-bold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                          ₹{order.amount}
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    {order.status !== 'completed' && order.status !== 'cancelled' && (
                      <div className="flex gap-1.5 mt-2 pt-2 border-t">
                        {order.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => updateOrderStatus(order.id, 'in_progress')}
                              className="flex-1 h-7 text-xs"
                            >
                              Start
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => updateOrderStatus(order.id, 'cancelled')}
                              className="h-7 text-xs px-2"
                            >
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {order.status === 'in_progress' && (
                          <Button 
                            size="sm" 
                            onClick={() => updateOrderStatus(order.id, 'completed')}
                            className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {orders.filter(o => o.status === 'pending').length}
            </p>
            <p className="text-sm text-muted-foreground">Yet to Start</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {orders.filter(o => o.status === 'in_progress').length}
            </p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {orders.filter(o => o.status === 'completed').length}
            </p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
              ₹{orders.filter(o => o.status === 'completed').reduce((sum, o) => sum + o.amount, 0)}
            </p>
            <p className="text-sm text-muted-foreground">Revenue</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
